import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Upload, Music } from "lucide-react";
import { toast } from "sonner";
import { uploadMediaFile } from "@/lib/media";
import api, { fileUrl } from "@/lib/api";

const STATIC_PAGES = [
  { label: "Home", path: "/" },
  { label: "Blog & Stories", path: "/blog" },
  { label: "Partner With Us", path: "/partner-with-us" },
];

export default function MusicSettings({ value, onChange, onPersist }) {
  const m = value || {};
  const tracks = m.tracks || [];
  const base = { enabled: false, volume: 35, tracks: [], ...m };
  const set = (patch) => onChange({ ...base, ...patch });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pageOptions, setPageOptions] = useState(STATIC_PAGES);

  useEffect(() => {
    Promise.allSettled([api.get("/admin/properties"), api.get("/admin/pages"), api.get("/admin/seo-pages")]).then(([p, cp, sp]) => {
      const opts = [...STATIC_PAGES];
      if (p.status === "fulfilled") (p.value.data || []).forEach((x) => opts.push({ label: x.name || x.slug, path: `/${x.slug}` }));
      if (cp.status === "fulfilled") (cp.value.data || []).forEach((x) => opts.push({ label: x.title || x.slug, path: `/${x.slug}` }));
      if (sp.status === "fulfilled") (sp.value.data || []).forEach((x) => opts.push({ label: x.title || x.h1 || x.slug, path: `/${x.slug}` }));
      const seen = new Set();
      setPageOptions(opts.filter((o) => o.path !== "/undefined" && !seen.has(o.path) && seen.add(o.path)));
    });
  }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const added = [];
    try {
      for (const f of files) {
        const url = await uploadMediaFile(f, setProgress);
        added.push({ url, title: f.name.replace(/\.[^.]+$/, ""), scope: "all", pages: [] });
      }
      const nextMusic = { ...base, tracks: [...tracks, ...added] };
      onChange(nextMusic);
      onPersist?.(nextMusic);
      toast.success(`${added.length} track${added.length === 1 ? "" : "s"} uploaded & saved`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setTrack = (i, patch) => set({ tracks: tracks.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const removeTrack = (i) => {
    const nextMusic = { ...base, tracks: tracks.filter((_, j) => j !== i) };
    onChange(nextMusic);
    onPersist?.(nextMusic);
  };
  const togglePage = (i, path) => {
    const cur = tracks[i].pages || [];
    setTrack(i, { pages: cur.includes(path) ? cur.filter((p) => p !== path) : [...cur, path] });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-zinc-600">
        Play soft ambient music across the public site. Guests get a floating music button with a caring note and can switch it off anytime (their choice is remembered).
      </p>

      <label className="flex items-center gap-3 text-sm">
        <Switch checked={!!m.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="music-enabled-toggle" />
        <span className="font-medium">Enable ambient music on the website</span>
      </label>

      {!!m.enabled && tracks.length === 0 && (
        <div className="border border-amber-300 bg-amber-50 text-amber-800 rounded-md px-4 py-3 text-sm" data-testid="music-empty-warning">
          Ambient music is <b>ON</b> but the playlist is empty — nothing will play on the website and the music button stays hidden. Upload at least one track below.
        </div>
      )}

      <div className="max-w-sm">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Volume</label>
          <span className="text-sm font-medium" data-testid="music-volume-value">{m.volume ?? 35}%</span>
        </div>
        <Slider className="mt-3" value={[m.volume ?? 35]} min={0} max={100} step={5} onValueChange={([v]) => set({ volume: v })} data-testid="music-volume-slider" />
        <p className="text-[11px] text-zinc-400 mt-2">Keep it gentle — 25–40% works best for ambience.</p>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Playlist ({tracks.length})</label>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 text-sm border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-50 disabled:opacity-60"
            data-testid="music-upload-btn"
          >
            <Upload className="w-4 h-4" /> {uploading ? `Uploading… ${progress}%` : "Upload tracks"}
          </button>
          <input ref={fileRef} type="file" accept="audio/*" multiple className="hidden" onChange={onFiles} data-testid="music-file-input" />
        </div>
        <p className="text-[11px] text-zinc-400 mt-1">
          MP3 recommended. Assign each track to all pages or specific pages — a page with its own tracks plays only those; other pages fall back to the &ldquo;All pages&rdquo; tracks.
        </p>
        <div className="mt-3 space-y-2">
          {tracks.length === 0 && (
            <div className="border border-dashed border-zinc-300 rounded-md p-6 text-center text-sm text-zinc-400">
              <Music className="w-5 h-5 mx-auto mb-2" /> No tracks yet — upload your first ambient track.
            </div>
          )}
          {tracks.map((t, i) => (
            <div key={i} className="border border-zinc-200 rounded-md p-3 bg-white space-y-3" data-testid={`music-track-${i}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-zinc-400 w-5">{i + 1}.</span>
                <Input className="flex-1 min-w-[140px]" value={t.title || ""} onChange={(e) => setTrack(i, { title: e.target.value })} placeholder="Track title" data-testid={`music-track-title-${i}`} />
                <audio controls preload="none" src={fileUrl(t.url)} className="h-9 max-w-[240px]" />
                <button onClick={() => removeTrack(i)} className="text-zinc-400 hover:text-red-600" aria-label="Remove track" data-testid={`music-track-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3 pl-8">
                <span className="text-xs text-zinc-500 uppercase tracking-wide">Playback</span>
                <Select value={t.play_mode || "loop"} onValueChange={(v) => setTrack(i, { play_mode: v })}>
                  <SelectTrigger className="w-[190px] h-8 text-sm" data-testid={`music-track-mode-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="loop">Loops with playlist</SelectItem>
                    <SelectItem value="once">Play once per visit</SelectItem>
                  </SelectContent>
                </Select>
                {(t.play_mode || "loop") === "once" && (
                  <span className="text-[11px] text-zinc-400">Ideal for voice-overs — plays first on arrival, then hands over to looping tracks.</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 pl-8">
                <span className="text-xs text-zinc-500 uppercase tracking-wide">Plays on</span>
                <Select value={t.scope || "all"} onValueChange={(v) => setTrack(i, { scope: v })}>
                  <SelectTrigger className="w-[170px] h-8 text-sm" data-testid={`music-track-scope-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All pages</SelectItem>
                    <SelectItem value="custom">Specific pages</SelectItem>
                  </SelectContent>
                </Select>
                {(t.scope || "all") === "custom" && (
                  <div className="flex flex-wrap gap-1.5" data-testid={`music-track-pages-${i}`}>
                    {pageOptions.map((o) => {
                      const on = (t.pages || []).includes(o.path);
                      return (
                        <button
                          key={o.path}
                          type="button"
                          onClick={() => togglePage(i, o.path)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}
                          data-testid={`music-track-${i}-page-${o.path.replace(/\//g, "_")}`}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                    {(t.pages || []).length === 0 && <span className="text-[11px] text-amber-600">Pick at least one page, or this track won&apos;t play anywhere.</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
